import React from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { Target, Heart, Users, Award, ArrowRight } from 'lucide-react';

const AboutVesta = () => {
  const { getCartItemCount } = useCart();

  const values = [
    {
      icon: Target,
      title: 'Purpose-Built',
      description: 'Every stitch, every seam, every feature is designed with women in trades in mind.'
    },
    {
      icon: Heart,
      title: 'Community-Driven',
      description: 'Built in collaboration with real women in the industry who know what works.'
    },
    {
      icon: Users,
      title: 'Inclusive',
      description: 'Comprehensive sizing and fit options because every woman deserves gear that fits.'
    },
    {
      icon: Award,
      title: 'Quality First',
      description: 'Premium materials and construction that last, not disposable fast fashion.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-gradient-to-br from-[#004D51] to-[#003338] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'url(/automotive-3.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="px-6 py-2 bg-[#DB5A14] text-white font-black text-sm tracking-widest">
                OUR STORY
              </div>
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Built by Women. <span className="text-[#F39C49]">For Women.</span>
            </h1>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto">
              Vesta was born from frustration—because safety, fit, and function should never be an afterthought.
            </p>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-black text-[#004D51] mb-6">
                  The Problem We Set Out to Solve
                </h2>
                <div className="space-y-4 text-[#545759] text-lg">
                  <p>
                    For too long, women in automotive refinishing have been forced to work in gear 
                    designed for men. Oversized suits that bunch up, drag through fresh paint, 
                    and restrict movement aren't just uncomfortable—they're a safety hazard.
                  </p>
                  <p>
                    Our founder, a collision repair shop owner and advocate for women in automotive, 
                    was frustrated by an industry that kept saying it wanted more women in the trades 
                    but failed to provide fit-for-purpose PPE. How can women feel valued when they 
                    can't even get workwear that fits?
                  </p>
                  <p>
                    She partnered with female technicians and apprentices across Australia to design 
                    what should have existed all along: professional workwear built for women's bodies.
                  </p>
                  <p className="font-bold text-[#004D51]">
                    Properly designed and fitted PPE isn't just about comfort—it's progress. 
                    Vesta is a statement that women belong in the trades and deserve gear that 
                    works as hard as they do.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-4 -left-4 w-full h-full bg-[#F39C49] rounded-2xl transform rotate-3"></div>
                <img
                  src="https://cdn.shopify.com/s/files/1/0770/5572/1703/files/160126_VestaWorkwear-69.jpg?v=1772281308"
                  alt="Women in trades"
                  className="relative rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-black text-[#004D51] mb-12 text-center">
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-6 text-center hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                <div className="w-16 h-16 bg-[#DB5A14] rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-black text-[#004D51] mb-3">{value.title}</h3>
                <p className="text-[#545759]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Name */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-black text-[#004D51] mb-6">
              Why <span className="text-[#DB5A14]">"Vesta"</span>?
            </h2>
            <p className="text-xl text-[#545759] mb-8">
              In Roman mythology, Vesta was the goddess of the hearth—the protector of home, 
              family, and fire. She represented the warmth at the center of every household, 
              the constant flame that kept everyone safe.
            </p>
            <p className="text-xl text-[#545759] mb-8">
              We chose this name because we see women in trades the same way: the unsung heroes 
              who keep workshops running, who protect quality and safety, who bring fire and 
              passion to their craft every single day.
            </p>
            <p className="text-lg text-[#DB5A14] italic font-medium">
              Vesta also has a personal connection to our founder… but that's a story for another day.
            </p>
          </div>
        </div>
      </section>

      {/* Australian Made */}
      <section className="py-24 bg-[#004D51] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-6xl mb-6">🇦🇺</div>
            <h2 className="text-4xl font-black mb-6">
              Proudly Australian
            </h2>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto mb-8">
              Designed in Australia with input from women in collision repair shops across the country. 
              We understand Australian conditions, Australian sizing, and the Australian trades community.
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
                <div className="text-3xl font-black text-[#F39C49] mb-2">100%</div>
                <p className="text-[#CFD2D4]">Australian Designed</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
                <div className="text-3xl font-black text-[#F39C49] mb-2">Real</div>
                <p className="text-[#CFD2D4]">Trades Input</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-6">
                <div className="text-3xl font-black text-[#F39C49] mb-2">Local</div>
                <p className="text-[#CFD2D4]">Support Team</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#DB5A14] to-[#F39C49] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-black mb-6">
              Ready to Experience the Difference?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join thousands of women who've upgraded their workwear game.
            </p>
            <Link to="/shop">
              <Button className="bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black px-8 py-6 text-lg">
                Shop the Collection <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default AboutVesta;
